
import Vue, { PropType } from "vue";
import { InputValidationRule, InputValidationRules } from "vuetify";

const isoRegex = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/;
const localRegex = /^([0-9]{1,2})[./-]([0-9]{1,2})[./-]([0-9]{4}$)/;
const allowedKeys = [
  "0",
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  // allow select all, copy, paste, cut
  "a",
  "A",
  "c",
  "C",
  "v",
  "V",
  "x",
  "X",
  // ".", // was previously included => WHY!?
  "ArrowLeft",
  "ArrowRight",
  "Backspace",
  "Delete",
  "End",
  "Enter",
  "Home",
  "Tab",
];

export default Vue.extend({
  props: {
    value: {
      type: [String, Date],
    },
    rules: {
      type: Array as PropType<InputValidationRules>,
      default: () => [] as InputValidationRules,
    },
    dense: Boolean,
    disabled: Boolean,
    label: String,
    hasToForceEmit: Boolean,
    hint: String,
    outlined: Boolean,
    persistentHint: Boolean,
    readonly: Boolean,
    backgroundColor: {
      type: String,
      required: false,
    },
    hideDetails: {
      type: [String, Boolean],
      required: false,
      default: false, // despite what is mentioned in doc, default value here was 'false'
    },
    maxDate: {
      type: String,
      required: false,
      default: undefined,
    },
    minDate: {
      type: String,
      required: false,
      default: undefined,
    },
  },
  inject: {
    form: { default: null },
  },
  data() {
    return {
      checkOnLoad: true,
      date: "",
      dateModel: false,
      formattedDate: "",
      hasFocus: false,
      isErrorShown: false,
      lastKey: null as string | null,
      showDatePicker: false,
    };
  },
  computed: {
    errorMessages(): string[] {
      if (!this.isErrorShown) {
        return [];
      }

      let baseErrorMsg: null | string[] = null;
      // baseRule is related to formattedDate (dd/mm/yyyy)
      const baseRuleEval = this.baseRule(this.formattedDate);
      if (typeof baseRuleEval === "string") {
        baseErrorMsg = [baseRuleEval];
      }
      // received rules are related to date (yyyy-mm-dd)
      const rulesInError = this.rules.filter(rule => {
        if (typeof rule === "string") {
          return true;
        }
        if (typeof rule === "function") {
          const ruleEvalValue = (rule as InputValidationRule)(this.date);
          return typeof ruleEvalValue === "string" || !ruleEvalValue;
        }
        return false;
      }) as InputValidationRule[];

      return (baseErrorMsg || []).concat(rulesInError.map(rule => rule(this.date)).flat() as string[]);
    },
    // hasError maintains form reactive validity (HOW?)
    hasError(): boolean {
      return this.rules.some(rule => {
        return typeof rule === "boolean"
          ? !rule
          : typeof rule === "string" || typeof rule(this.date) === "string" || !rule(this.date);
      });
    },
    shouldValidate(): boolean {
      return !this.readonly && !this.disabled;
    },
  },
  methods: {
    baseRule(v: string) {
      const value = (v || "").trim();
      const match = value.match(localRegex);

      if (match) {
        const [d, m, y] = [parseInt(match[1]), parseInt(match[2]), parseInt(match[3])];
        const maxDay = new Date(y, m, 0).getDate();
        if (
          y === 0 ||
          m === 0 ||
          m > 12 ||
          d > maxDay ||
          // TODO: remove de là !! => set validation rule ouski faut
          (this.maxDate && new Date(y, m - 1, d) > new Date(this.maxDate))
        ) {
          return this.$tc("validation.date");
        }
      } else if (value) {
        return this.$tc("validation.date");
      }
      return true;
    },
    clearDate() {
      this.formattedDate = "";
      this.validate();
      this.$emit("input", undefined);
    },
    dateToISOString(date: Date) {
      // new Date with cancellation of timezoneOffset before transformation into ISOString
      return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, -14);
    },
    formatDatePickerTitleDate(date: string) {
      return new Date(date).toLocaleDateString(`${this.$i18n.locale}-BE`, {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    },
    onBlur() {
      this.hasFocus = false;
      if (this.lastKey === "Tab") {
        this.showDatePicker = false;
      }
      if (!this.isErrorShown) {
        setTimeout(() => {
          this.isErrorShown = true;
        }, 100);
      }
    },
    onDatePickerInput(value: string) {
      (this.$refs.vTextField as any).focus();
      this.showDatePicker = false;
      this.$emit("input", this.dateModel ? new Date(value) : value);
    },
    onFocus() {
      this.hasFocus = true;
      this.lastKey = null;
    },
    onIconClick() {
      this.showDatePicker = !this.showDatePicker;
      (this.$refs.vTextField as any).focus();
    },
    onKeyDown(e: KeyboardEvent) {
      this.lastKey = e.key;
      if (e.key === "Enter") {
        this.showDatePicker = !this.showDatePicker;
      } else if (!allowedKeys.includes(e.key)) {
        e.preventDefault();
      }
    },
    onKeyUp(e: KeyboardEvent) {
      if (
        ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"].includes(e.key) &&
        (this.formattedDate.match(/^[0-3]{1}[0-9]{1}$/) ||
          this.formattedDate.match(/^[0-3]{1}[0-9]{1}\/[0-1]{1}[0-9]{1}$/))
      ) {
        this.formattedDate = `${this.formattedDate}/`;
      }
    },
    onTextInput(value: string) {
      this.isErrorShown = true;
      value = (value || "").trim();
      const match = value.match(localRegex);

      if (match) {
        const [d, m, y] = [parseInt(match[1]), parseInt(match[2]), parseInt(match[3])];
        const maxDay = new Date(y, m, 0).getDate();

        if (y !== 0 && m !== 0 && m <= 12 && d <= maxDay) {
          const dateToEmit = value.replace(localRegex, "$3-$2-$1");
          this.$emit("input", this.dateModel ? new Date(dateToEmit) : dateToEmit);
        }
      } else if (this.hasFocus && value === "") {
        this.$emit("input", undefined);
      }
    },
    // v-form methods
    async reset() {
      await this.$nextTick();
      this.resetValidation();
    },
    resetValidation() {
      this.isErrorShown = false;
    },
    validate(): boolean {
      this.isErrorShown = true;
      return !this.hasError;
    },
  },
  watch: {
    hasToForceEmit(hasToForceEmit) {
      if (hasToForceEmit) {
        this.$emit("input", this.dateModel ? new Date(this.date) : this.date);
      }
    },
    value: {
      immediate: true,
      async handler(newValue) {
        if (this.checkOnLoad) {
          if (newValue instanceof Date) {
            newValue = this.dateToISOString(newValue);
            // if dateModel: emit a Date instance @input (onTextInput && onDatePickerInput)
            // => ? don't know if can happen / may crash
            this.dateModel = true;
            console.log("dateModel => emit a Date instance @input");
          } else if (typeof newValue === "string" && newValue !== "") {
            if (newValue.match(/^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}/)) {
              // assuming value is formatted with timezone
              newValue = newValue.slice(0, 10);
            }
            if (newValue.match(/^[0-9]{2}\/[0-9]{2}\/[0-9]{4}$/)) {
              // assuming date is in 'dd/mm/yyyy' format
              const [day, month, year] = newValue.split("/");
              // creating new Date instance based on 'yyyy-mm-dd' format
              const date = new Date(`${year}-${month}-${day}`);
              // if conversion is valid (!NaN)
              if (!isNaN(date.getTime())) {
                newValue = this.dateToISOString(date);
              }
            }
          }
          this.checkOnLoad = false;
        }
        this.date = newValue;
        this.formattedDate = this.date && this.date.replace(isoRegex, "$3/$2/$1");
      },
    },
  },
  created() {
    const frm = (this as any).form;
    frm && frm.register(this);
  },
  beforeDestroy() {
    const frm = (this as any).form;
    frm && frm.unregister(this);
  },
});
